import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import type { Namespace } from './namespaces';

/**
 * Message descriptors (#2109 R3-B3).
 *
 * A pure `lib/*.ts` builder (`buildParentPatterns`, `buildChips`,
 * `formatRecurrenceLabel`, …) never returns an English sentence and never
 * takes a `TFunction`. It returns `{ key, params }`, and the component
 * resolves it at render with `useMsg()`. A descriptor re-renders on a
 * language switch; a string baked when the builder ran would not.
 *
 * `key` is always the full `namespace:key` path (`'tasks:recurrence.every'`),
 * typed against the generated resources, so a descriptor resolves the same
 * whichever namespace the rendering component bound. A counted phrase passes
 * `count` in `params` and names the plural BASE (`'common:items'`).
 */
export type TKey = ParseKeys<Namespace[]>;

export interface Msg {
  key: TKey;
  params?: Record<string, unknown>;
}

/** Build a descriptor. An empty or absent `params` is dropped, so `toEqual` assertions stay simple. */
export function msg(key: TKey, params?: Record<string, unknown>): Msg {
  return params && Object.keys(params).length > 0 ? { key, params } : { key };
}

type LooseT = (key: string, params?: Record<string, unknown>) => string;

/**
 * Any `t`: the hook’s `t` bound to any namespace (`useTranslation('tasks').t`)
 * or a fixed-language `i18n.getFixedT('en')`. The helpers only ever pass a
 * full `namespace:key` path, so the binding does not matter to them.
 */
export type AnyTFunction = (...args: never[]) => unknown;

/** Resolve a descriptor with a given `t` (a fixed-language `t` in tests, the hook’s `t` in components). `null` → `''`. */
export function resolveMsg(t: AnyTFunction, m: Msg | null | undefined): string {
  if (!m) return '';
  return (t as unknown as LooseT)(m.key, m.params);
}

/**
 * The render-time resolver. `const resolve = useMsg();` then `resolve(chip.label)`.
 * The component subscribes to language changes through `useTranslation`, so
 * every descriptor it resolves follows a switch without a reload.
 */
export function useMsg(): (m: Msg | null | undefined) => string {
  const { t } = useTranslation();
  return useCallback((m: Msg | null | undefined) => resolveMsg(t, m), [t]);
}
